/**
 * App Runtime Service
 * 
 * Manages app-wide runtime state including:
 * - Query parameters (clubId, tenantHandle, userId)
 * - Monitoring initialization and sync
 * - Pre-React initialization
 * 
 * This service is initialized BEFORE React renders to ensure
 * all core services are ready when components mount.
 */

import { 
  initMonitoring,
  setClientContext,
  getProviderInstance
} from '@butter/monitoring/web';
import type { QueryParamKey, ParamChangeListener, AppRuntimeConfig } from './types';

// Query parameter keys
export const TENANT_HANDLE_PARAM: QueryParamKey = 'tenant-handle';
export const CLUB_ID_PARAM: QueryParamKey = 'club-id';
export const USER_ID_PARAM: QueryParamKey = 'user-id';

// All supported query parameters
export const ALL_QUERY_PARAMS: QueryParamKey[] = [
  TENANT_HANDLE_PARAM,
  CLUB_ID_PARAM,
  USER_ID_PARAM
];

/**
 * AppRuntimeService manages app-wide runtime state and initialization
 * Singleton pattern ensures one instance across the application
 */
class AppRuntimeService {
  private _params: Map<QueryParamKey, string | null> = new Map();
  private _listeners: Map<QueryParamKey, Set<ParamChangeListener>> = new Map();
  private _initialized: boolean = false;
  private _monitoringEnabled: boolean = false;
  // @ts-expect-error: Unused var for future debug needs
  private _debug: boolean = false;

  /**
   * Initialize the app runtime service
   * Call this BEFORE React renders (in main.tsx)
   */
  initialize(config?: AppRuntimeConfig): void {
    if (this._initialized) {
      console.warn('[AppRuntime] Already initialized');
      return;
    }

    this._debug = config?.debug ?? false;

    // 1. Initialize monitoring (if DSN provided)
    this.initializeMonitoring(config);

    // 2. Read parameters from URL
    this.syncFromUrl();
    
    // 3. Sync initial tenant context to monitoring
    this.syncMonitoringContext();

    // 4. Set up navigation tracking
    this.setupNavigationTracking();

    this._initialized = true;
  }

  /**
   * Initialize monitoring service
   */
  private initializeMonitoring(config?: AppRuntimeConfig): void {
    const dsn = config?.sentryDsn;
    
    if (!dsn) {
      return;
    }

    try {
      // Initialize Sentry via monitoring package
      initMonitoring({
        service: config?.appName || 'web-portal-2',
        dsn,
        environment: config?.environment || 'development',
      });
      
      // Set initial client context
      setClientContext({
        app: 'web-portal',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        platform: 'web',
      });

      this._monitoringEnabled = true;
    } catch (error) {
      console.error('[AppRuntime] Failed to initialize monitoring:', error);
    }
  }

  /**
   * Sync tenant context to monitoring
   * 
   * Since we only have clubId/tenantHandle from URL (not full tenant context),
   * we set these as tags directly on the monitoring provider.
   */
  private syncMonitoringContext(): void {
    if (!this._monitoringEnabled) return;

    const clubIdStr = this.clubId;
    const tenantHandle = this.tenantHandle;

    // Only sync if we have at least one value
    if (clubIdStr || tenantHandle) {
      try {
        const provider = getProviderInstance();
        if (!provider) return;

        // Set as tags for filtering in Sentry
        if (clubIdStr) {
          provider.setTag('club_id', clubIdStr);
        }
        if (tenantHandle) {
          provider.setTag('tenant_handle', tenantHandle);
        }
      } catch (error) {
        console.error('[AppRuntime] Failed to sync monitoring context:', error);
      }
    }
  }

  /**
   * Set up event listeners to keep parameters in sync during navigation
   */
  private setupNavigationTracking(): void {
    // Listen for history changes (back/forward navigation)
    window.addEventListener('popstate', () => {
      this.syncFromUrl();
      this.syncMonitoringContext();
    });

    // Intercept pushState and replaceState to track SPA navigation
    // This ensures appRuntime stays in sync with React Router navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      const result = originalPushState.apply(this, args);
      // Sync after pushState completes
      window.dispatchEvent(new Event('pushstate'));
      return result;
    };

    window.history.replaceState = function(...args) {
      const result = originalReplaceState.apply(this, args);
      // Sync after replaceState completes
      window.dispatchEvent(new Event('replacestate'));
      return result;
    };

    // Listen for custom events triggered by intercepted methods
    window.addEventListener('pushstate', () => {
      this.syncFromUrl();
      this.syncMonitoringContext();
    });

    window.addEventListener('replacestate', () => {
      this.syncFromUrl();
      this.syncMonitoringContext();
    });
  }

  /**
   * Update internal state from URL parameters
   */
  private syncFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);

    ALL_QUERY_PARAMS.forEach(key => {
      const value = urlParams.get(key);
      const currentValue = this._params.get(key);
      
      // Only update if value has changed
      if (value !== currentValue) {
        this._params.set(key, value);
        this.notifyListeners(key, value);
      }
    });
  }

  /**
   * Notify listeners of parameter changes
   */
  private notifyListeners(key: QueryParamKey, value: string | null): void {
    const listeners = this._listeners.get(key);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(value);
        } catch (error) {
          console.error('[AppRuntime] Error in listener:', error);
        }
      });
    }

    // Auto-sync monitoring when tenant-related params change
    if (key === CLUB_ID_PARAM || key === TENANT_HANDLE_PARAM) {
      this.syncMonitoringContext();
    }
  }

  /**
   * Get a parameter value
   */
  get(key: QueryParamKey): string | null {
    // If not initialized yet, try to read directly from URL
    if (!this._initialized) {
      console.warn('[AppRuntime] Not initialized, reading directly from URL');
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(key);
    }
    return this._params.get(key) || null;
  }

  /**
   * Set a parameter value
   * Updates both internal state and URL
   */
  set(key: QueryParamKey, value: string | null): void {
    // Update internal state
    this._params.set(key, value);
    
    // Update URL without reloading the page
    const url = new URL(window.location.href);
    if (value === null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    
    window.history.replaceState({}, '', url.toString());
    this.notifyListeners(key, value);
  }

  /**
   * Check if a parameter exists and has a value
   */
  has(key: QueryParamKey): boolean {
    return this._params.has(key) && this._params.get(key) !== null;
  }

  /**
   * Subscribe to parameter changes
   * Returns an unsubscribe function
   */
  subscribe(key: QueryParamKey, listener: ParamChangeListener): () => void {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    
    const listeners = this._listeners.get(key)!;
    listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this._listeners.delete(key);
      }
    };
  }

  /**
   * Get the current tenant handle
   */
  get tenantHandle(): string | null {
    return this.get(TENANT_HANDLE_PARAM);
  }

  /**
   * Get the current club ID
   */
  get clubId(): string | null {
    return this.get(CLUB_ID_PARAM);
  }

  /**
   * Get the current user ID
   */
  get userId(): string | null {
    return this.get(USER_ID_PARAM);
  }

  /**
   * Get the parameter key mapping for a given camelCase name
   */
  getKeyByName(name: 'tenantHandle' | 'clubId' | 'userId'): QueryParamKey | null {
    switch(name) {
      case 'tenantHandle': return TENANT_HANDLE_PARAM;
      case 'clubId': return CLUB_ID_PARAM;
      case 'userId': return USER_ID_PARAM;
      default: return null;
    }
  }

  /**
   * Sync all current parameters back to the URL
   */
  syncToUrl(): void {
    const url = new URL(window.location.href);
    
    // Remove all existing supported parameters first
    ALL_QUERY_PARAMS.forEach(key => {
      url.searchParams.delete(key);
    });
    
    // Add current parameter values
    this._params.forEach((value, key) => {
      if (value !== null) {
        url.searchParams.set(key, value);
      }
    });
    
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Check if the service has been initialized
   */
  get isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Check if monitoring is enabled
   */
  get isMonitoringEnabled(): boolean {
    return this._monitoringEnabled;
  }
}

// Export singleton instance
export const appRuntime = new AppRuntimeService();
